import { createRng, type Rng } from '../../../core/rng';
import { lerpColor } from '../../color';
import { hsl, type World as Look } from '../../world';
import { calmAt, calmCenter, glowDot, grain, rgba, sketchCircle, vignette, type Painter, type Spot } from '../../art/paint';

const TAU = Math.PI * 2;

interface Tones {
  readonly bg: number;
  readonly far: number;
  readonly ceil: number;
  readonly ink: number;
  readonly rim: number;
  readonly light: number;
  readonly a1: number;
  readonly a2: number;
}

interface Cap {
  readonly x: number;
  readonly base: number;
  readonly r: number;
  readonly fade: boolean;
}

interface Layout {
  readonly portrait: boolean;
  readonly caps: readonly Cap[];
  readonly moon: { readonly x: number; readonly y: number; readonly r: number };
  readonly ground: number;
  readonly prop: number;
}

interface Trunk {
  readonly x: number;
  readonly width: number;
  readonly lean: number;
}

function tonesOf(look: Look): Tones {
  const pal = look.palette;
  return { bg: pal.bg, far: hsl(look.hue, 28, 16), ceil: look.ceil, ink: pal.ink, rim: pal.rim, light: pal.incoming.circle, a1: pal.held, a2: pal.spot };
}

function layoutOf(p: Painter): Layout {
  const { w, h, unit } = p;
  if (w / h < 0.8) {
    const r = Math.min(0.3 * w, 0.12 * h);
    const mr = Math.min(0.12 * w, 0.06 * h);
    return {
      portrait: true,
      caps: [{ x: r * 1.06 + 0.03 * w, base: 0.2 * h + r * 0.84 + 10, r, fade: true }],
      moon: { x: w - mr * 1.3 - 0.03 * w, y: 0.2 * h + mr + 14, r: mr },
      ground: h * 0.9,
      prop: Math.min(w * 0.6, unit) / unit,
    };
  }
  const band = Math.max(0, (w - 2 * unit) / 2);
  const r = Math.max(unit * 0.24, Math.min(band * 0.62, unit * 0.38));
  const r2 = r * 0.7;
  const mr = r * 0.62;
  return {
    portrait: false,
    caps: [
      { x: r * 1.06 + 0.012 * w, base: 0.5 * h, r, fade: false },
      { x: w - r2 * 1.1 - 0.014 * w, base: 0.7 * h, r: r2, fade: false },
    ],
    moon: { x: w - Math.max(mr * 1.35, band * 0.5), y: 0.2 * h + mr + 0.04 * h, r: mr },
    ground: h * 0.9,
    prop: 1,
  };
}

function haze(p: Painter, t: Tones, rng: Rng): void {
  const ctx = p.ctx;
  const g = ctx.createLinearGradient(0, 0, 0, p.h);
  g.addColorStop(0, rgba(lerpColor(t.bg, t.ink, 0.45)));
  g.addColorStop(0.45, rgba(t.bg));
  g.addColorStop(1, rgba(lerpColor(t.bg, t.far, 0.8)));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, p.w, p.h);
  for (let i = 0; i < 6; i++) {
    glowDot(ctx, rng.float() * p.w, p.h * rng.range(0.15, 0.85), p.unit * rng.range(0.5, 0.9), t.far, 0.35);
  }
}

function moon(p: Painter, t: Tones, m: Layout['moon'], rng: Rng): void {
  const ctx = p.ctx;
  glowDot(ctx, m.x, m.y, m.r * 3.2, lerpColor(t.ceil, t.light, 0.35), 0.16);
  glowDot(ctx, m.x, m.y, m.r * 1.7, lerpColor(t.ceil, t.rim, 0.3), 0.14);
  const disc = ctx.createRadialGradient(m.x - m.r * 0.3, m.y - m.r * 0.3, m.r * 0.1, m.x, m.y, m.r);
  disc.addColorStop(0, rgba(lerpColor(t.ceil, t.rim, 0.22)));
  disc.addColorStop(0.7, rgba(lerpColor(t.ceil, t.rim, 0.1)));
  disc.addColorStop(1, rgba(lerpColor(t.ceil, t.rim, 0.04)));
  ctx.fillStyle = disc;
  ctx.beginPath();
  ctx.arc(m.x, m.y, m.r, 0, TAU);
  ctx.fill();
  ctx.save();
  ctx.clip();
  ctx.fillStyle = rgba(lerpColor(t.ceil, t.far, 0.3), 0.32);
  for (let i = 0; i < 3; i++) {
    const a = rng.float() * TAU;
    const d = m.r * rng.range(0.1, 0.65);
    ctx.beginPath();
    ctx.ellipse(m.x + Math.cos(a) * d, m.y + Math.sin(a) * d, m.r * rng.range(0.1, 0.24), m.r * rng.range(0.08, 0.18), rng.float() * Math.PI, 0, TAU);
    ctx.fill();
  }
  ctx.restore();
  ctx.strokeStyle = rgba(t.rim, 0.3);
  ctx.lineWidth = Math.max(1.5, m.r * 0.03);
  sketchCircle(ctx, m.x, m.y, m.r, rng, 0.012);
}

function trunksOf(p: Painter, layout: Layout, rng: Rng): Trunk[] {
  const n = layout.portrait ? 6 : 11;
  const out: Trunk[] = [];
  for (let i = 0; i < n; i++) {
    out.push({
      x: ((i + rng.range(0.15, 0.85)) / n) * p.w,
      width: p.unit * rng.range(0.03, 0.07) * (layout.portrait ? 0.8 : 1),
      lean: rng.range(-0.04, 0.04) * p.h,
    });
  }
  return out;
}

function trunks(p: Painter, t: Tones, list: readonly Trunk[], layout: Layout, rng: Rng): void {
  const ctx = p.ctx;
  for (const tr of list) {
    const middle = !layout.portrait && Math.abs(tr.x - p.cx) < p.unit * 0.95;
    const tone = lerpColor(t.bg, t.far, rng.range(0.6, 1));
    const hw = tr.width / 2;
    const foot = layout.ground + 4;
    ctx.fillStyle = rgba(tone, middle ? 0.5 : 0.95);
    ctx.beginPath();
    ctx.moveTo(tr.x + tr.lean - hw, -10);
    ctx.quadraticCurveTo(tr.x - hw * 0.9, p.h * 0.5, tr.x - hw * 1.1, foot - hw);
    ctx.quadraticCurveTo(tr.x - hw * 1.3, foot, tr.x - hw * 2.1, foot + 6);
    ctx.lineTo(tr.x + hw * 2.1, foot + 6);
    ctx.quadraticCurveTo(tr.x + hw * 1.3, foot, tr.x + hw * 1.1, foot - hw);
    ctx.quadraticCurveTo(tr.x + hw * 0.9, p.h * 0.5, tr.x + tr.lean + hw, -10);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = rgba(t.ink, middle ? 0.12 : 0.22);
    ctx.lineWidth = 1;
    for (let k = 0; k < 2; k++) {
      const off = rng.range(-0.5, 0.5) * hw;
      const y0 = p.h * rng.range(0.05, 0.4);
      const y1 = y0 + p.h * rng.range(0.15, 0.35);
      ctx.beginPath();
      ctx.moveTo(tr.x + off + tr.lean * (1 - y0 / p.h), y0);
      ctx.quadraticCurveTo(tr.x + off + rng.range(-2, 2), (y0 + y1) / 2, tr.x + off + tr.lean * (1 - y1 / p.h), y1);
      ctx.stroke();
    }
  }
}

function mist(p: Painter, t: Tones, y: number, depth: number): void {
  const ctx = p.ctx;
  const g = ctx.createLinearGradient(0, y - depth, 0, y + depth);
  g.addColorStop(0, rgba(t.far, 0));
  g.addColorStop(0.5, rgba(lerpColor(t.far, t.ceil, 0.2), 0.45));
  g.addColorStop(1, rgba(t.far, 0));
  ctx.fillStyle = g;
  ctx.fillRect(0, y - depth, p.w, depth * 2);
}

function domePath(x: number, base: number, r: number): Path2D {
  const top = base - r * 0.8;
  const d = new Path2D();
  d.moveTo(x - r, base);
  d.bezierCurveTo(x - r * 1.02, top - r * 0.06, x + r * 1.02, top - r * 0.06, x + r, base);
  d.quadraticCurveTo(x, base + r * 0.14, x - r, base);
  d.closePath();
  return d;
}

function giantCap(p: Painter, t: Tones, cap: Cap, rng: Rng): void {
  const ctx = p.ctx;
  const { x, base, r } = cap;
  const sw = r * 0.3;
  const foot = cap.fade ? base + r * 1.5 : p.h + 10;
  const stem = lerpColor(t.far, t.ceil, 0.5);
  const sg = ctx.createLinearGradient(0, base, 0, foot);
  sg.addColorStop(0, rgba(stem));
  sg.addColorStop(1, cap.fade ? rgba(stem, 0) : rgba(lerpColor(stem, t.bg, 0.35)));
  ctx.fillStyle = sg;
  ctx.beginPath();
  ctx.moveTo(x - sw * 0.5, base);
  ctx.quadraticCurveTo(x - sw * 0.68, (base + foot) / 2, x - sw * 0.8, foot);
  ctx.lineTo(x + sw * 0.85, foot);
  ctx.quadraticCurveTo(x + sw * 0.62, (base + foot) / 2, x + sw * 0.5, base);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = rgba(lerpColor(stem, t.rim, 0.1));
  ctx.beginPath();
  ctx.ellipse(x, base + r * 0.24, sw * 0.78, sw * 0.2, 0, 0, TAU);
  ctx.fill();
  glowDot(ctx, x, base + r * 0.12, r * 0.95, t.light, 0.07);
  ctx.fillStyle = rgba(lerpColor(t.bg, t.far, 0.5));
  ctx.beginPath();
  ctx.ellipse(x, base + r * 0.04, r * 0.97, r * 0.16, 0, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(t.ink, 0.35);
  ctx.lineWidth = Math.max(1, r * 0.008);
  ctx.beginPath();
  for (let i = 1; i < 26; i++) {
    const a = (i / 26) * Math.PI;
    ctx.moveTo(x + Math.cos(a) * r * 0.2, base + r * 0.03 + Math.sin(a) * r * 0.03);
    ctx.lineTo(x + Math.cos(a) * r * 0.94, base + r * 0.04 + Math.sin(a) * r * 0.14);
  }
  ctx.stroke();
  const dome = domePath(x, base, r);
  const top = base - r * 0.8;
  ctx.fillStyle = rgba(t.ceil);
  ctx.fill(dome);
  ctx.save();
  ctx.clip(dome);
  const shade = ctx.createRadialGradient(x - r * 0.35, top + r * 0.25, r * 0.08, x - r * 0.1, base - r * 0.2, r * 1.25);
  shade.addColorStop(0, rgba(lerpColor(t.ceil, t.rim, 0.12)));
  shade.addColorStop(0.55, rgba(t.ceil, 0));
  shade.addColorStop(1, rgba(lerpColor(t.ceil, t.ink, 0.55), 0.6));
  ctx.fillStyle = shade;
  ctx.fillRect(x - r * 1.1, top - r * 0.2, r * 2.2, r * 1.2);
  const n = 8;
  for (let i = 0; i < n; i++) {
    const u = ((i + rng.range(0.2, 0.8)) / n) * 1.7 - 0.85;
    const v = rng.range(0.2, 0.8);
    const sr = r * rng.range(0.06, 0.12) * (1 - Math.abs(u) * 0.45);
    const py = base - r * 0.82 * Math.sqrt(Math.max(0, 1 - u * u)) * v;
    glowDot(ctx, x + u * r * 0.9, py, sr * 2.2, t.light, 0.12);
    ctx.fillStyle = rgba(lerpColor(t.ceil, t.light, 0.32), 0.85);
    ctx.beginPath();
    ctx.ellipse(x + u * r * 0.9, py, sr, sr * (0.5 + 0.4 * v), -u * 0.5, 0, TAU);
    ctx.fill();
  }
  ctx.restore();
  ctx.strokeStyle = rgba(t.ink, 0.9);
  ctx.lineWidth = Math.max(2, r * 0.025);
  ctx.lineJoin = 'round';
  ctx.stroke(dome);
  ctx.strokeStyle = rgba(t.rim, 0.2);
  ctx.lineWidth = Math.max(1.5, r * 0.016);
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.ellipse(x, base, r * 0.9, r * 0.72, 0, Math.PI * 1.12, Math.PI * 1.45);
  ctx.stroke();
}

function ground(p: Painter, t: Tones, layout: Layout, rng: Rng): void {
  const ctx = p.ctx;
  const top = layout.ground;
  ctx.fillStyle = rgba(lerpColor(t.bg, t.ink, 0.35));
  ctx.beginPath();
  ctx.moveTo(-10, p.h + 10);
  ctx.lineTo(-10, top);
  const step = p.w / 9;
  for (let x = 0; x <= p.w + step; x += step) {
    ctx.quadraticCurveTo(x - step * 0.5, top - p.unit * rng.range(0.02, 0.06), x, top + p.unit * rng.range(-0.01, 0.02));
  }
  ctx.lineTo(p.w + 10, p.h + 10);
  ctx.closePath();
  ctx.fill();
}

function fern(p: Painter, at: Spot, len: number, angle: number, curl: number, tone: number): void {
  const ctx = p.ctx;
  const n = 14;
  const step = len / n;
  let x = at.x;
  let y = at.y;
  ctx.strokeStyle = rgba(tone, 0.9);
  ctx.fillStyle = rgba(tone, 0.85);
  ctx.lineWidth = Math.max(1.5, len * 0.014);
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x, y);
  const pts: number[] = [];
  for (let i = 1; i <= n; i++) {
    const a = angle + (curl * i) / n;
    x += Math.cos(a) * step;
    y += Math.sin(a) * step;
    ctx.lineTo(x, y);
    pts.push(x, y, a);
  }
  ctx.stroke();
  for (let i = 1; i < n; i++) {
    const px = pts[i * 3]!;
    const py = pts[i * 3 + 1]!;
    const a = pts[i * 3 + 2]!;
    const leaf = len * (0.17 * (1 - i / n) + 0.03);
    for (const side of [-1, 1]) {
      const la = a + side * (Math.PI / 2 - 0.55);
      ctx.beginPath();
      ctx.ellipse(px + Math.cos(la) * leaf * 0.5, py + Math.sin(la) * leaf * 0.5, leaf * 0.5, leaf * 0.15, la, 0, TAU);
      ctx.fill();
    }
  }
}

function shroom(p: Painter, t: Tones, x: number, y: number, s: number, tone: number): void {
  const ctx = p.ctx;
  glowDot(ctx, x, y - s * 0.9, s * 3.2, tone, 0.26);
  ctx.fillStyle = rgba(lerpColor(t.rim, tone, 0.3), 0.45);
  ctx.beginPath();
  ctx.moveTo(x - s * 0.22, y);
  ctx.quadraticCurveTo(x - s * 0.32, y - s * 0.5, x - s * 0.17, y - s * 0.95);
  ctx.lineTo(x + s * 0.17, y - s * 0.95);
  ctx.quadraticCurveTo(x + s * 0.32, y - s * 0.5, x + s * 0.22, y);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = rgba(tone, 0.6);
  ctx.beginPath();
  ctx.moveTo(x - s * 0.78, y - s * 0.85);
  ctx.bezierCurveTo(x - s * 0.75, y - s * 1.75, x + s * 0.75, y - s * 1.75, x + s * 0.78, y - s * 0.85);
  ctx.quadraticCurveTo(x, y - s * 0.72, x - s * 0.78, y - s * 0.85);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = rgba(t.rim, 0.45);
  ctx.beginPath();
  ctx.arc(x - s * 0.25, y - s * 1.22, s * 0.11, 0, TAU);
  ctx.arc(x + s * 0.28, y - s * 1.1, s * 0.08, 0, TAU);
  ctx.fill();
}

function corner(p: Painter, t: Tones, layout: Layout, left: boolean, rng: Rng): void {
  const dir = left ? 1 : -1;
  const edge = left ? 0 : p.w;
  const base = layout.ground + p.unit * 0.02;
  const len = p.unit * 0.34 * layout.prop;
  const tone = lerpColor(t.far, t.ceil, 0.35);
  for (let i = 0; i < 4; i++) {
    const at = { x: edge + dir * p.unit * rng.range(0.02, 0.2) * layout.prop, y: base + rng.range(0, 0.03) * p.unit };
    const angle = left ? -Math.PI / 2 + rng.range(0.15, 0.9) : -Math.PI / 2 - rng.range(0.15, 0.9);
    fern(p, at, len * rng.range(0.7, 1.1), angle, dir * rng.range(0.5, 0.9), tone);
  }
  const count = 4;
  for (let i = 0; i < count; i++) {
    const s = p.unit * rng.range(0.025, 0.048) * layout.prop;
    const x = edge + dir * p.unit * (0.06 + 0.07 * i + rng.range(0, 0.03)) * layout.prop;
    shroom(p, t, x, base - rng.range(0, 0.02) * p.unit, s, i % 2 === 0 ? t.a1 : t.a2);
  }
}

function spores(p: Painter, t: Tones): void {
  const ctx = p.ctx;
  const tones = [t.light, t.a2, t.rim];
  for (let i = 0; i < 110; i++) {
    const x = p.rng.float() * p.w;
    const y = p.rng.float() * p.h;
    if (calmAt(p, x, y, 0.85)) continue;
    ctx.fillStyle = rgba(p.rng.pick(tones), p.rng.range(0.15, 0.45));
    ctx.beginPath();
    ctx.arc(x, y, p.rng.range(0.6, 1.6), 0, TAU);
    ctx.fill();
  }
  for (let i = 0; i < 10; i++) {
    const x = p.rng.float() * p.w;
    const y = p.h * p.rng.range(0.22, 0.86);
    if (calmAt(p, x, y, 0.95)) continue;
    const tone = p.rng.chance(0.5) ? t.a2 : t.light;
    glowDot(ctx, x, y, p.rng.range(6, 11), tone, 0.3);
    ctx.fillStyle = rgba(tone, 0.5);
    ctx.beginPath();
    ctx.arc(x, y, 1.6, 0, TAU);
    ctx.fill();
  }
}

function brackets(p: Painter, t: Tones, list: readonly Trunk[], layout: Layout): void {
  const ctx = p.ctx;
  const outside = list.filter((tr) => layout.portrait || Math.abs(tr.x - p.cx) >= p.unit * 0.95);
  for (const tr of outside.slice(0, 3)) {
    const y0 = p.h * p.rng.range(0.4, 0.65);
    for (let k = 0; k < 3; k++) {
      const y = y0 + k * tr.width * 0.55;
      const side = k % 2 === 0 ? 1 : -1;
      const sx = tr.x + side * tr.width * 0.45;
      const sr = tr.width * (0.7 - k * 0.12);
      if (calmAt(p, sx, y, 0.9)) continue;
      ctx.fillStyle = rgba(lerpColor(t.far, t.ceil, 0.7));
      ctx.beginPath();
      ctx.ellipse(sx, y, sr, sr * 0.38, 0, Math.PI, TAU);
      ctx.closePath();
      ctx.fill();
      ctx.strokeStyle = rgba(t.a2, 0.35);
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(sx - sr, y);
      ctx.lineTo(sx + sr, y);
      ctx.stroke();
    }
  }
}

function fairyRing(p: Painter, t: Tones, layout: Layout): void {
  const left = layout.portrait || p.rng.chance(0.5);
  const rx = p.unit * 0.16 * layout.prop;
  const cx = left ? p.w - rx * 1.6 - p.unit * 0.04 : rx * 1.6 + p.unit * 0.04;
  const cy = layout.ground + p.unit * 0.01;
  const n = 9;
  for (let i = 0; i < n; i++) {
    const a = (i / n) * TAU + 0.3;
    if (Math.sin(a) > 0.2 && i % 2 === 0) continue;
    const x = cx + Math.cos(a) * rx;
    const y = cy + Math.sin(a) * rx * 0.28;
    shroom(p, t, x, y, p.unit * 0.022 * layout.prop * (0.85 + 0.25 * Math.sin(a)), i % 3 === 0 ? t.light : t.a2);
  }
}

function glowWorms(p: Painter, t: Tones): void {
  const ctx = p.ctx;
  for (let i = 0; i < 9; i++) {
    const x = p.rng.float() * p.w;
    const len = p.h * p.rng.range(0.06, 0.16);
    if (calmAt(p, x, len, 1)) continue;
    ctx.strokeStyle = rgba(t.rim, 0.12);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, len);
    ctx.stroke();
    glowDot(ctx, x, len, 7, t.a2, 0.35);
  }
}

export function paint(p: Painter, look: Look): void {
  const t = tonesOf(look);
  const layout = layoutOf(p);
  const wood = createRng(`${look.seed}-wood`);
  haze(p, t, wood.fork('haze'));
  moon(p, t, layout.moon, wood.fork('moon'));
  const list = trunksOf(p, layout, wood.fork('trunks'));
  trunks(p, t, list, layout, wood.fork('bark'));
  mist(p, t, p.h * 0.72, p.h * 0.12);
  if (p.level >= 2) brackets(p, t, list, layout);
  const capRng = wood.fork('caps');
  for (const cap of layout.caps) giantCap(p, t, cap, capRng);
  ground(p, t, layout, wood.fork('ground'));
  corner(p, t, layout, true, wood.fork('left'));
  corner(p, t, layout, false, wood.fork('right'));
  if (p.level >= 3) fairyRing(p, t, layout);
  spores(p, t);
  if (p.level >= 4) glowWorms(p, t);
  calmCenter(p, t.bg, 0.6, 1.15);
  grain(p, 0.05, true);
  vignette(p, t.ink, 0.45);
}
